import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type Handsontable from 'handsontable';
import { Button, Tooltip } from 'antd';
import { SaveOutlined, EditOutlined, ClearOutlined, LoadingOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HandsontableWrapperSimple,
  type HandsontableWrapperRef,
  CELL_CLS,
  createNumericColumn,
  createTextColumn,
} from '../../components/handsontable';
import { TRACKING_CLASS_USER } from '../../components/handsontable/tracking/utils/classNameCache';
import barStyles from '../../styles/components/FloatingActionBar.module.scss';

// ---------------------------------------------------------------------------
// Демо-таблица «Динамический план продаж» для главной страницы.
// Не подключена к API — это чистый презентационный пример эргономики:
// сворачивание колонок, фильтры, цветовая индикация отклонений,
// подсветка изменений + плавающий тулбар «Сохранить / Отменить».
// Данные моковые.
// ---------------------------------------------------------------------------

type DemoCell = string | number | null;
type DemoRow = DemoCell[];

const MOCK_DATA: DemoRow[] = [
  ['Лобанова Т.',  'Аптека «Здоровье»',    'БАЙЕР',     'АДЕМПАС',   'Адемпас Таблетки',     62598.45, 50,  35,  -15,  10, 8,  10, 9],
  ['Кухаренко А.', 'Фарма-Елизово ООО',    'ПРОМОМЕД',  'АПАЛУТАМИ', 'Апалутамид Таблетки',  72502.10, 0,   0,   0,    0,  0,  0,  0],
  ['Лобанова Т.',  'Петропавловск-Камч.',  'ВИФОР',     'МАЛЬТОФЕР', 'Мальтофер Таблетки',   386.74,   300, 210, -90,  60, 55, 60, 58],
  ['Иванов С.',    'Озёрновская б-ца',     'CSL BEHRING','РЕЗОГАМ Н','Резогам Раствор',      5500,     10,  7,   -3,   3,  3,  3,  2],
  ['Лобанова Т.',  'Усть-Большерецкая',    'ВИФОР',     'МАЛЬТОФЕР', 'Мальтофер Фол.',       737,      50,  35,  -15,  10, 11, 10, 9],
  ['Кухаренко А.', 'Аптека «Будь здоров»', 'АМДЖЕН',    'ВЕКТИБИКС', 'Вектибикс Концентрат', 24700,    8,   8,   0,    2,  2,  2,  2],
  ['Иванов С.',    'Камчатская краевая',   'АМДЖЕН',    'КИПРОЛИС',  'Кипролис Лиофилизат',  60000,    4,   3,   -1,   1,  1,  1,  1],
  ['Лобанова Т.',  'Фарма-Елизово ООО',    'САНОФИ',    'ДУПИКСЕНТ', 'Дупиксент Раствор',    80913.11, 2,   2,   0,    1,  1,  1,  0],
  ['Петрова О.',   'Корякская окружная',   'АМДЖЕН',    'ПАРСАБИВ',  'Парсабив Раствор',     4983.95,  20,  14,  -6,   5,  4,  5,  5],
  ['Иванов С.',    'Аптека «Семейная»',    'АМДЖЕН',    'РЕПАТА',    'Репата Раствор',       12800,    12,  9,   -3,   3,  3,  3,  2],
  ['Петрова О.',   'Аптека «Витамин»',     'АМДЖЕН',    'ЭНПЛЕЙТ',   'Энплейт Лиофилизат',   38000,    6,   6,   0,    2,  2,  2,  1],
  ['Лобанова Т.',  'Фарма-Елизово ООО',    'АМЕДАРТ',   'АПАЛУТАМ',  'Апалутамид-Амедарт',   72502.10, 4,   3,   -1,   1,  1,  1,  1],
  ['Кухаренко А.', 'Петропавловск-Камч.',  'АСТРАЗЕНЕКА','ИНЫЕ ТН',  'Инфильтрат Профил.',   0,        0,   0,   0,    0,  0,  0,  0],
  ['Иванов С.',    'Озёрновская б-ца',     'БАЙЕР',     'ГАДОВИСТ',  'Гадовист Раствор',     0,        15,  12,  -3,   4,  4,  4,  3],
  ['Петрова О.',   'Камчатская краевая',   'БАЙЕР',     'НУБЕКА',    'Нубека Таблетки',      118000,   3,   3,   0,    1,  1,  1,  1],
];

// Индексы колонок данных (физические)
const COL_DEVIATION = 8;
const COL_DYN_PLAN = 7;
const EDITABLE_COLS = new Set<number>([7, 9, 11]); // Дин.План, Янв-План, Фев-План

// collapsible: true — даёт месячным группам кнопку сворачивания. HoT v16 поддерживает,
// но в типах TS это поле отсутствует — кастуем явно, как делается в основной таблице.
const NESTED_HEADERS = [
  [
    { label: 'Показатели', colspan: 9 },
    { label: 'Январь', colspan: 2, collapsible: true },
    { label: 'Февраль', colspan: 2, collapsible: true },
  ],
  [
    'Менеджер',
    'Клиент',
    'ЦПР',
    'ТН',
    'SKU',
    'Цена продажи',
    'Утв.План',
    'Дин.План',
    'Отклонение',
    'План',
    'Факт',
    'План',
    'Факт',
  ],
] as unknown as Handsontable.GridSettings['nestedHeaders'];

const COLUMN_DEFS: Handsontable.ColumnSettings[] = [
  { data: 0, width: 130, ...createTextColumn({ readOnly: true }) },
  { data: 1, width: 180, ...createTextColumn({ readOnly: true }) },
  { data: 2, width: 110, ...createTextColumn({ readOnly: true }) },
  { data: 3, width: 110, ...createTextColumn({ readOnly: true }) },
  { data: 4, width: 170, ...createTextColumn({ readOnly: true }) },
  { data: 5, width: 120, ...createNumericColumn({ align: 'htCenter', readOnly: true, pattern: '0,0.00' }) },
  { data: 6, width: 100, ...createNumericColumn({ align: 'htCenter', readOnly: true, pattern: '0,0' }) },
  { data: 7, width: 100, ...createNumericColumn({ align: 'htCenter', pattern: '0,0' }) },
  { data: 8, width: 110, ...createNumericColumn({ align: 'htCenter', readOnly: true, pattern: '0,0' }) },
  { data: 9, width: 80, ...createNumericColumn({ align: 'htCenter', pattern: '0,0' }) },
  { data: 10, width: 80, ...createNumericColumn({ align: 'htCenter', readOnly: true, pattern: '0,0' }) },
  { data: 11, width: 80, ...createNumericColumn({ align: 'htCenter', pattern: '0,0' }) },
  { data: 12, width: 80, ...createNumericColumn({ align: 'htCenter', readOnly: true, pattern: '0,0' }) },
];

const cloneRows = (rows: DemoRow[]): DemoRow[] => rows.map((r) => [...r]);

const cellsEqual = (a: DemoCell, b: DemoCell): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
};

export const ErgonomicsDemoTable: React.FC = () => {
  // Live-данные таблицы (HoT мутирует array on-edit).
  const dataRef = useRef<DemoRow[]>(cloneRows(MOCK_DATA));
  // Снимок «как было» — против него считаем изменения и откатываем.
  const baselineRef = useRef<DemoRow[]>(cloneRows(MOCK_DATA));
  // Set ключей "row:col" для подсветки. Перерисовка триггерится через setState.
  const [changedCells, setChangedCells] = useState<Set<string>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const tableRef = useRef<HandsontableWrapperRef>(null);

  const recomputeChanges = useCallback(() => {
    const next = new Set<string>();
    for (let r = 0; r < dataRef.current.length; r++) {
      for (let c = 0; c < dataRef.current[r].length; c++) {
        if (!cellsEqual(dataRef.current[r][c], baselineRef.current[r][c])) {
          next.add(`${r}:${c}`);
        }
      }
    }
    setChangedCells(next);
  }, []);

  // После каждой смены changedCells — прогоняем HoT render(), чтобы cells()
  // переоценился и подсветка обновилась мгновенно.
  useEffect(() => {
    tableRef.current?.getHotInstance()?.render();
  }, [changedCells]);

  const handleAfterChange = useCallback(
    (changes: (Handsontable.CellChange | null)[] | null, source: Handsontable.ChangeSource) => {
      if (source === 'loadData' || !changes) return;

      // Если поменяли Дин.План — пересчитываем Отклонение = Дин.План − Утв.План
      // прямо в data ref, потом просим HoT перерисоваться.
      const hot = tableRef.current?.getHotInstance();
      let needsRender = false;
      for (const change of changes) {
        if (!change) continue;
        const [visualRow, prop] = change;
        const physicalCol = typeof prop === 'number' ? prop : Number(prop);
        if (physicalCol !== COL_DYN_PLAN) continue;
        const physicalRow = hot?.toPhysicalRow(visualRow) ?? visualRow;
        const row = dataRef.current[physicalRow];
        if (!row) continue;
        const approved = Number(row[6]) || 0;
        const dyn = Number(row[COL_DYN_PLAN]) || 0;
        row[COL_DEVIATION] = Math.round((dyn - approved) * 100) / 100;
        needsRender = true;
      }
      if (needsRender) hot?.render();

      recomputeChanges();
    },
    [recomputeChanges],
  );

  const handleSave = useCallback(() => {
    // Имитируем сетевую задержку, чтобы кнопка показала свой loading-стейт.
    setSaving(true);
    setTimeout(() => {
      baselineRef.current = cloneRows(dataRef.current);
      setChangedCells(new Set());
      setSaving(false);
      tableRef.current?.getHotInstance()?.render();
    }, 500);
  }, []);

  const handleReset = useCallback(() => {
    dataRef.current = cloneRows(baselineRef.current);
    const hot = tableRef.current?.getHotInstance();
    if (hot) {
      hot.loadData(dataRef.current);
    }
    setChangedCells(new Set());
  }, []);

  // ВНИМАНИЕ: `cells(row, col)` в Handsontable отдаёт ФИЗИЧЕСКИЕ индексы —
  // никакого `toPhysicalRow`/`toPhysicalColumn` и `getDataAtCell` (визуальный API).
  const cells = useCallback(
    function (this: Handsontable.CellProperties, row: number, col: number) {
      const meta: Handsontable.CellMeta = {};
      const hot = this.instance;
      if (!hot) return meta;

      const classes: string[] = [];

      if (col === COL_DYN_PLAN) classes.push(CELL_CLS.highlightedHeader);

      if (col === COL_DEVIATION) {
        const raw = hot.getSourceDataAtCell(row, col);
        const v = Number(raw);
        if (!Number.isNaN(v) && v !== 0) {
          classes.push(v < 0 ? CELL_CLS.deviationNegative : CELL_CLS.deviationPositive);
        }
      }

      if (changedCells.has(`${row}:${col}`)) {
        classes.push(TRACKING_CLASS_USER);
      }

      if (!EDITABLE_COLS.has(col)) {
        meta.readOnly = true;
      }

      if (classes.length > 0) meta.className = classes.join(' ');
      return meta;
    },
    [changedCells],
  );

  const hotSettings = useMemo<Partial<Handsontable.GridSettings>>(
    () => ({
      filters: true,
      dropdownMenu: ['filter_by_value', 'filter_action_bar'],
      manualColumnResize: true,
      columnSorting: true,
      collapsibleColumns: true,
      contextMenu: ['copy'],
    }),
    [],
  );

  const changeCount = changedCells.size;

  return (
    <div style={{ position: 'relative' }}>
      <HandsontableWrapperSimple
        ref={tableRef}
        tableId="demo-dynamic-plan"
        data={dataRef.current}
        columns={COLUMN_DEFS}
        nestedHeaders={NESTED_HEADERS}
        colHeaders={true}
        rowHeaders={true}
        cells={cells}
        height={420}
        stretchH="none"
        hotSettings={hotSettings}
        onAfterChange={handleAfterChange}
        pagination={{
          pageSize: 10,
          pageSizeList: [10, 15, 25],
          showPageSize: true,
          showCounter: true,
          showNavigation: true,
        }}
      />

      {/* Плавающий тулбар — копирует FloatingActionBar из приложения */}
      <DemoFloatingBar
        visible={changeCount > 0}
        count={changeCount}
        saving={saving}
        onSave={handleSave}
        onReset={handleReset}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// DemoFloatingBar — визуально идентичен боевому FloatingActionBar
// (теже SCSS-классы), но без зависимости от ToolbarContext / RBAC.
// ---------------------------------------------------------------------------

const BAR_VARIANTS = {
  hidden: { opacity: 0, y: 40, x: '-50%' },
  visible: { opacity: 1, y: 0, x: '-50%' },
  exit: { opacity: 0, y: 40, x: '-50%' },
};

const BAR_TRANSITION = { type: 'spring' as const, stiffness: 400, damping: 30 };

const DemoFloatingBar: React.FC<{
  visible: boolean;
  count: number;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
}> = ({ visible, count, saving, onSave, onReset }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        className={barStyles.floatingBar}
        variants={BAR_VARIANTS}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={BAR_TRANSITION}
      >
        <div className={barStyles.changesBadge}>
          <EditOutlined style={{ fontSize: 13 }} />
          <span>Несохранённые изменения</span>
          <span className={barStyles.changesCount}>{count}</span>
        </div>

        <span className={barStyles.divider} />

        <Tooltip title="Сбросить все изменения">
          <Button
            icon={saving ? <LoadingOutlined /> : <ClearOutlined />}
            onClick={onReset}
            disabled={saving}
            size="middle"
            className={barStyles.discardBtn}
          />
        </Tooltip>

        <Tooltip title={`Сохранить ${count} ${count === 1 ? 'изменение' : 'изменений'}`}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={saving}
            size="middle"
            className={barStyles.saveBtn}
          >
            Сохранить
          </Button>
        </Tooltip>
      </motion.div>
    )}
  </AnimatePresence>
);

ErgonomicsDemoTable.displayName = 'ErgonomicsDemoTable';
